import { createApiRef, useApi, AnalyticsContext, useAnalytics, configApiRef } from '@backstage/core-plugin-api';
import React, { useMemo, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { makeStyles, TextField, Chip, FormControl, FormLabel, FormControlLabel, Checkbox, InputLabel, Select, MenuItem, Button, InputBase, InputAdornment, IconButton, ListItem, ListItemIcon, ListItemText, Box, Divider } from '@material-ui/core';
import { createVersionedContext, createVersionedValueMap } from '@backstage/version-bridge';
import useAsync from 'react-use/lib/useAsync';
import usePrevious from 'react-use/lib/usePrevious';
import { Autocomplete } from '@material-ui/lab';
import useAsyncFn from 'react-use/lib/useAsyncFn';
import useDebounce from 'react-use/lib/useDebounce';
import { Progress, ResponseErrorPanel, EmptyState, Link } from '@backstage/core-components';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import SearchIcon from '@material-ui/icons/Search';
import ClearButton from '@material-ui/icons/Clear';

const searchApiRef = createApiRef({
  id: "plugin.search.queryservice"
});
class MockSearchApi {
  constructor(mockedResults) {
    this.mockedResults = mockedResults;
  }
  query() {
    return Promise.resolve(this.mockedResults || { results: [] });
  }
}

const useStyles$2 = makeStyles(
  () => ({
    highlight: {}
  }),
  { name: "BackstageHighlightedSearchResultText" }
);
const HighlightedSearchResultText = ({
  text,
  preTag,
  postTag
}) => {
  const classes = useStyles$2();
  const terms = useMemo(
    () => text.split(new RegExp(`(${preTag}.+?${postTag})`)),
    [postTag, preTag, text]
  );
  return /* @__PURE__ */ React.createElement(React.Fragment, null, terms.map(
    (t, idx) => t.includes(preTag) ? /* @__PURE__ */ React.createElement("mark", {
      className: classes.highlight,
      key: idx
    }, t.replace(new RegExp(`${preTag}|${postTag}`, "g"), "")) : t
  ));
};

const SearchContext = createVersionedContext("search-context");
const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error("useSearch must be used within a SearchContextProvider");
  }
  const value = context.atVersion(1);
  if (!value) {
    throw new Error("No SearchContext v1 found");
  }
  return value;
};
const useSearchContextCheck = () => {
  const context = useContext(SearchContext);
  return context !== void 0;
};
const searchInitialState = {
  term: "",
  pageCursor: void 0,
  filters: {},
  types: []
};
const SearchContextProvider = (props) => {
  var _a, _b, _c, _d;
  const { initialState = searchInitialState, children } = props;
  const searchApi = useApi(searchApiRef);
  const [pageCursor, setPageCursor] = useState(
    initialState.pageCursor
  );
  const [filters, setFilters] = useState(initialState.filters);
  const [term, setTerm] = useState(initialState.term);
  const [types, setTypes] = useState(initialState.types);
  const prevTerm = usePrevious(term);
  const result = useAsync(
    () => searchApi.query({
      term,
      filters,
      pageCursor,
      types
    }),
    [term, filters, types, pageCursor]
  );
  const hasNextPage = !result.loading && !result.error && ((_a = result.value) == null ? void 0 : _a.nextPageCursor);
  const hasPreviousPage = !result.loading && !result.error && ((_b = result.value) == null ? void 0 : _b.previousPageCursor);
  const fetchNextPage = useCallback(() => {
    var _a2;
    setPageCursor((_a2 = result.value) == null ? void 0 : _a2.nextPageCursor);
  }, [(_c = result.value) == null ? void 0 : _c.nextPageCursor]);
  const fetchPreviousPage = useCallback(() => {
    var _a2;
    setPageCursor((_a2 = result.value) == null ? void 0 : _a2.previousPageCursor);
  }, [(_d = result.value) == null ? void 0 : _d.previousPageCursor]);
  useEffect(() => {
    if (prevTerm !== void 0 && term !== prevTerm) {
      setPageCursor(void 0);
    }
  }, [term, prevTerm, setPageCursor]);
  const value = {
    result,
    filters,
    setFilters,
    term,
    setTerm,
    types,
    setTypes,
    pageCursor,
    setPageCursor,
    fetchNextPage: hasNextPage ? fetchNextPage : void 0,
    fetchPreviousPage: hasPreviousPage ? fetchPreviousPage : void 0
  };
  const versionedValue = createVersionedValueMap({ 1: value });
  return /* @__PURE__ */ React.createElement(AnalyticsContext, {
    attributes: { searchTypes: types.sort().join(",") }
  }, /* @__PURE__ */ React.createElement(SearchContext.Provider, {
    value: versionedValue,
    children
  }));
};

const useAsyncFilterValues = (fn, inputValue, defaultValues = [], debounce = 250) => {
  const valuesMemo = useRef({});
  const definiteFn = fn || (() => Promise.resolve([]));
  const [state, callback] = useAsyncFn(definiteFn, [inputValue], {
    loading: true
  });
  useDebounce(
    () => {
      if (valuesMemo.current[inputValue] === void 0) {
        valuesMemo.current[inputValue] = callback(inputValue).then((values) => {
          valuesMemo.current[inputValue] = values;
          return values;
        });
      }
    },
    debounce,
    [callback, inputValue]
  );
  if (defaultValues.length) {
    return {
      loading: false,
      value: defaultValues
    };
  }
  const possibleValue = valuesMemo.current[inputValue];
  if (Array.isArray(possibleValue)) {
    return {
      loading: false,
      value: possibleValue
    };
  }
  return state;
};
const useDefaultFilterValue = (name, defaultValue) => {
  const { setFilters } = useSearch();
  useEffect(() => {
    if (defaultValue && [defaultValue].flat().length > 0) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        [name]: defaultValue
      }));
    }
  }, []);
};

const AutocompleteFilter = (props) => {
  const {
    className,
    defaultValue,
    name,
    values: givenValues,
    valuesDebounceMs,
    label,
    filterSelectedOptions,
    limitTags,
    multiple
  } = props;
  const [inputValue, setInputValue] = useState("");
  useDefaultFilterValue(name, defaultValue);
  const asyncValues = typeof givenValues === "function" ? givenValues : void 0;
  const defaultValues = typeof givenValues === "function" ? void 0 : givenValues;
  const { value: values, loading } = useAsyncFilterValues(
    asyncValues,
    inputValue,
    defaultValues,
    valuesDebounceMs
  );
  const { filters, setFilters } = useSearch();
  const filterValue = filters[name] || (multiple ? [] : null);
  const handleChange = (_, newValue) => {
    setFilters((prevState) => {
      const { [name]: filter, ...others } = prevState;
      if (newValue) {
        return { ...others, [name]: newValue };
      }
      return { ...others };
    });
  };
  const renderInput = (params) => /* @__PURE__ */ React.createElement(TextField, {
    ...params,
    name: "search",
    variant: "outlined",
    label,
    fullWidth: true
  });
  const renderTags = (tagValue, getTagProps) => tagValue.map((option, index) => /* @__PURE__ */ React.createElement(Chip, {
    label: option,
    color: "primary",
    ...getTagProps({ index })
  }));
  return /* @__PURE__ */ React.createElement(Autocomplete, {
    filterSelectedOptions,
    limitTags,
    multiple,
    className,
    id: `${multiple ? "multi-" : ""}select-filter-${name}--select`,
    options: values || [],
    loading,
    value: filterValue,
    onChange: handleChange,
    onInputChange: (_, newValue) => setInputValue(newValue),
    renderInput,
    renderTags
  });
};

const useStyles$1 = makeStyles({
  label: {
    textTransform: "capitalize"
  }
});
const CheckboxFilter = (props) => {
  const {
    className,
    defaultValue,
    label,
    name,
    values: givenValues = [],
    valuesDebounceMs
  } = props;
  const classes = useStyles$1();
  const { filters, setFilters } = useSearch();
  useDefaultFilterValue(name, defaultValue);
  const asyncValues = typeof givenValues === "function" ? givenValues : void 0;
  const defaultValues = typeof givenValues === "function" ? void 0 : givenValues;
  const { value: values = [], loading } = useAsyncFilterValues(
    asyncValues,
    "",
    defaultValues,
    valuesDebounceMs
  );
  const handleChange = (e) => {
    const {
      target: { value, checked }
    } = e;
    setFilters((prevFilters) => {
      const { [name]: filter, ...others } = prevFilters;
      const rest = (filter || []).filter((i) => i !== value);
      const items = checked ? [...rest, value] : rest;
      return items.length ? { ...others, [name]: items } : others;
    });
  };
  return /* @__PURE__ */ React.createElement(FormControl, {
    className,
    disabled: loading,
    fullWidth: true,
    "data-testid": "search-checkboxfilter-next"
  }, label ? /* @__PURE__ */ React.createElement(FormLabel, {
    className: classes.label
  }, label) : null, values.map((value) => {
    var _a;
    return /* @__PURE__ */ React.createElement(FormControlLabel, {
      key: value,
      control: /* @__PURE__ */ React.createElement(Checkbox, {
        color: "primary",
        tabIndex: -1,
        inputProps: { "aria-labelledby": value },
        value,
        name: value,
        onChange: handleChange,
        checked: ((_a = filters[name]) != null ? _a : []).includes(value)
      }),
      label: value
    });
  }));
};
const SelectFilter = (props) => {
  const {
    className,
    defaultValue,
    label,
    name,
    values: givenValues,
    valuesDebounceMs
  } = props;
  const classes = useStyles$1();
  useDefaultFilterValue(name, defaultValue);
  const asyncValues = typeof givenValues === "function" ? givenValues : void 0;
  const defaultValues = typeof givenValues === "function" ? void 0 : givenValues;
  const { value: values = [], loading } = useAsyncFilterValues(
    asyncValues,
    "",
    defaultValues,
    valuesDebounceMs
  );
  const { filters, setFilters } = useSearch();
  const handleChange = (e) => {
    const {
      target: { value }
    } = e;
    setFilters((prevFilters) => {
      const { [name]: filter, ...others } = prevFilters;
      return value ? { ...others, [name]: value } : others;
    });
  };
  return /* @__PURE__ */ React.createElement(FormControl, {
    disabled: loading,
    className,
    variant: "filled",
    fullWidth: true,
    "data-testid": "search-selectfilter-next"
  }, label ? /* @__PURE__ */ React.createElement(InputLabel, {
    className: classes.label,
    margin: "dense"
  }, label) : null, /* @__PURE__ */ React.createElement(Select, {
    variant: "outlined",
    value: filters[name] || "",
    onChange: handleChange
  }, /* @__PURE__ */ React.createElement(MenuItem, {
    value: ""
  }, /* @__PURE__ */ React.createElement("em", null, "All")), values.map((value) => /* @__PURE__ */ React.createElement(MenuItem, {
    key: value,
    value
  }, value))));
};
const SearchFilter = ({
  component: Element,
  ...props
}) => /* @__PURE__ */ React.createElement(Element, {
  ...props
});
SearchFilter.Checkbox = (props) => /* @__PURE__ */ React.createElement(SearchFilter, {
  ...props,
  component: CheckboxFilter
});
SearchFilter.Select = (props) => /* @__PURE__ */ React.createElement(SearchFilter, {
  ...props,
  component: SelectFilter
});
SearchFilter.Autocomplete = (props) => /* @__PURE__ */ React.createElement(SearchFilter, {
  ...props,
  component: AutocompleteFilter
});

const SearchResultComponent = ({ children }) => {
  const {
    result: { loading, error, value }
  } = useSearch();
  if (loading) {
    return /* @__PURE__ */ React.createElement(Progress, null);
  }
  if (error) {
    return /* @__PURE__ */ React.createElement(ResponseErrorPanel, {
      title: "Error encountered while fetching search results",
      error
    });
  }
  if (!(value == null ? void 0 : value.results.length)) {
    return /* @__PURE__ */ React.createElement(EmptyState, {
      missing: "data",
      title: "Sorry, no results were found"
    });
  }
  return /* @__PURE__ */ React.createElement(React.Fragment, null, children({ results: value.results }));
};
const HigherOrderSearchResult = (props) => {
  return /* @__PURE__ */ React.createElement(AnalyticsContext, {
    attributes: {
      pluginId: "search",
      extension: "SearchResult"
    }
  }, /* @__PURE__ */ React.createElement(SearchResultComponent, {
    ...props
  }));
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    margin: theme.spacing(2, 0)
  }
}));
const SearchResultPager = () => {
  const { fetchNextPage, fetchPreviousPage } = useSearch();
  const classes = useStyles();
  if (!fetchNextPage && !fetchPreviousPage) {
    return /* @__PURE__ */ React.createElement(React.Fragment, null);
  }
  return /* @__PURE__ */ React.createElement("nav", {
    "arial-label": "pagination navigation",
    className: classes.root
  }, /* @__PURE__ */ React.createElement(Button, {
    "aria-label": "previous page",
    disabled: !fetchPreviousPage,
    onClick: fetchPreviousPage,
    startIcon: /* @__PURE__ */ React.createElement(ArrowBackIosIcon, null)
  }, "Previous"), /* @__PURE__ */ React.createElement(Button, {
    "aria-label": "next page",
    disabled: !fetchNextPage,
    onClick: fetchNextPage,
    endIcon: /* @__PURE__ */ React.createElement(ArrowForwardIosIcon, null)
  }, "Next"));
};

const TrackSearch = ({ children }) => {
  const analytics = useAnalytics();
  const { term } = useSearch();
  useEffect(() => {
    if (term) {
      analytics.captureEvent("search", term);
    }
  }, [analytics, term]);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, children);
};

const SearchBarBase = ({
  onChange,
  onKeyDown,
  onSubmit,
  debounceTime = 200,
  clearButton = true,
  fullWidth = true,
  value: defaultValue,
  inputProps: defaultInputProps = {},
  endAdornment: defaultEndAdornment,
  ...props
}) => {
  const configApi = useApi(configApiRef);
  const [value, setValue] = useState(defaultValue);
  const hasSearchContext = useSearchContextCheck();
  useEffect(() => {
    setValue(
      (prevValue) => prevValue !== defaultValue ? defaultValue : prevValue
    );
  }, [defaultValue]);
  useDebounce(() => onChange(value), debounceTime, [value]);
  const handleChange = useCallback(
    (e) => {
      setValue(e.target.value);
    },
    [setValue]
  );
  const handleKeyDown = useCallback(
    (e) => {
      if (onKeyDown)
        onKeyDown(e);
      if (onSubmit && e.key === "Enter") {
        onSubmit();
      }
    },
    [onKeyDown, onSubmit]
  );
  const handleClear = useCallback(() => {
    onChange("");
  }, [onChange]);
  const placeholder = `Search in ${configApi.getOptionalString("app.title") || "Backstage"}`;
  const startAdornment = /* @__PURE__ */ React.createElement(InputAdornment, {
    position: "start"
  }, /* @__PURE__ */ React.createElement(IconButton, {
    "aria-label": "Query",
    disabled: true
  }, /* @__PURE__ */ React.createElement(SearchIcon, null)));
  const endAdornment = /* @__PURE__ */ React.createElement(InputAdornment, {
    position: "end"
  }, /* @__PURE__ */ React.createElement(IconButton, {
    "aria-label": "Clear",
    onClick: handleClear
  }, /* @__PURE__ */ React.createElement(ClearButton, null)));
  const searchBar = /* @__PURE__ */ React.createElement(TrackSearch, null, /* @__PURE__ */ React.createElement(InputBase, {
    "data-testid": "search-bar-next",
    value,
    placeholder,
    startAdornment,
    endAdornment: clearButton ? endAdornment : defaultEndAdornment,
    inputProps: { "aria-label": "Search", ...defaultInputProps },
    fullWidth,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    ...props
  }));
  return hasSearchContext ? searchBar : /* @__PURE__ */ React.createElement(SearchContextProvider, null, searchBar);
};
const SearchBar = ({ onChange, ...props }) => {
  const { term, setTerm } = useSearch();
  const handleChange = useCallback(
    (newValue) => {
      if (onChange) {
        onChange(newValue);
      } else {
        setTerm(newValue);
      }
    },
    [onChange, setTerm]
  );
  return /* @__PURE__ */ React.createElement(AnalyticsContext, {
    attributes: { pluginId: "search", extension: "SearchBar" }
  }, /* @__PURE__ */ React.createElement(SearchBarBase, {
    value: term,
    onChange: handleChange,
    ...props
  }));
};

const DefaultResultListItemComponent = ({
  result,
  highlight,
  rank,
  icon,
  secondaryAction,
  lineClamp = 5
}) => {
  const analytics = useAnalytics();
  const handleClick = () => {
    analytics.captureEvent("discover", result.title, {
      attributes: { to: result.location },
      value: rank
    });
  };
  return /* @__PURE__ */ React.createElement(Link, {
    noTrack: true,
    to: result.location,
    onClick: handleClick
  }, /* @__PURE__ */ React.createElement(ListItem, {
    alignItems: "center"
  }, icon && /* @__PURE__ */ React.createElement(ListItemIcon, null, icon), /* @__PURE__ */ React.createElement(ListItemText, {
    primaryTypographyProps: { variant: "h6" },
    primary: (highlight == null ? void 0 : highlight.fields.title) ? /* @__PURE__ */ React.createElement(HighlightedSearchResultText, {
      text: highlight.fields.title,
      preTag: highlight.preTag,
      postTag: highlight.postTag
    }) : result.title,
    secondary: /* @__PURE__ */ React.createElement("span", {
      style: {
        display: "-webkit-box",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: lineClamp,
        overflow: "hidden"
      }
    }, (highlight == null ? void 0 : highlight.fields.text) ? /* @__PURE__ */ React.createElement(HighlightedSearchResultText, {
      text: highlight.fields.text,
      preTag: highlight.preTag,
      postTag: highlight.postTag
    }) : result.text)
  }), secondaryAction && /* @__PURE__ */ React.createElement(Box, {
    alignItems: "flex-end"
  }, secondaryAction)), /* @__PURE__ */ React.createElement(Divider, null));
};
const HigherOrderDefaultResultListItem = (props) => {
  return /* @__PURE__ */ React.createElement(AnalyticsContext, {
    attributes: {
      pluginId: "search",
      extension: "DefaultResultListItem"
    }
  }, /* @__PURE__ */ React.createElement(DefaultResultListItemComponent, {
    ...props
  }));
};

export { AutocompleteFilter, CheckboxFilter, HigherOrderDefaultResultListItem as DefaultResultListItem, HighlightedSearchResultText, MockSearchApi, SearchBar, SearchBarBase, SearchContextProvider, SearchFilter, HigherOrderSearchResult as SearchResult, SearchResultComponent, SearchResultPager, SelectFilter, searchApiRef, useSearch, useSearchContextCheck };
//# sourceMappingURL=index.esm.js.map
